import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { NavigationContainer, useNavigation, useRoute, useIsFocused, getStateFromPath } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { LoginScreen } from '../screens/Login';
import { Home } from '../screens/Home';
import { Profile } from '../screens/Profile';
import { VenueList } from '../screens/VenueList';
import { VenueScreen } from '../screens/VenueScreen';
import { VenueSubmission } from '../screens/VenueSubmission';
import { Admin } from '../screens/Admin';
import { TourPlanner } from '../screens/TourPlanner';
import { LandingScreen } from '../screens/LandingScreen';
import { HeaderBar } from '../components/HeaderBar';
import { Footer } from '../components/Footer';
import { View, Dimensions, Platform, Text, ScrollView, ActivityIndicator, Image, Pressable } from 'react-native';
import { config } from '../config/environment';
import { LoginPersistenceProvider, useUser } from '../components/LoginPersistence';
import { VenueProvider } from '../components/VenueContext';
import { CommunityModalProvider } from '../contexts/CommunityModalContext';
import { CommunityProvider } from '../contexts/CommunityContext';
import { VersionWidgetProvider } from '../contexts/VersionWidgetContext';
import { VersionWidget } from '../components/VersionWidget';
import TesterSwitcher from '../components/TesterSwitcher';
import ReviewVrcButton from '../components/ReviewVrcButton';
import { WhatsNewProvider } from '../contexts/WhatsNewContext';
import { AddVenue } from '../screens/AddVenue';
import { MyReports } from '../screens/MyReports';
import { WaitlistScreen } from '../screens/WaitlistScreen';
import { AboutUs } from '../screens/AboutUs';
import { PrivacyPolicy, TermsOfUse } from '../screens/LegalPage';
import { MutualAidFund } from '../screens/MutualAidFund';
import { ReviewNotes } from '../screens/ReviewNotes';
import { NotifyWhenLive } from '../screens/NotifyWhenLive';
import { useComingSoonFeatureUnlocked } from '../contexts/FeatureFlagsContext';
import { Plans } from '../screens/Plans';
import { SubscriptionSuccess } from '../screens/SubscriptionSuccess';
import { ManageSubscription } from '../screens/ManageSubscription';
import { Balance } from '../screens/Balance';


import { Contact } from '../screens/Contact';
import { CommunityScreen } from '../screens/CommunityScreen';
import CommunityList from '../screens/CommunityList';
import CommunityPostCreate from '../screens/CommunityPostCreate';
import ProfileCardEdit from '../screens/ProfileCardEdit';
import GearSwapList from '../screens/GearSwapList';
import EventsList from '../screens/EventsList';
import GearSwapPostCreate from '../screens/GearSwapPostCreate';
import { MyCalendar } from '../screens/MyCalendar';
import SharedTourView from '../screens/SharedTourView';
import { CalendarDayDetails } from '../screens/CalendarDayDetails';
import CalendarShareView from '../screens/CalendarShareView';
import UserProfileScreen from '../screens/UserProfileScreen';
import OrganizationProfileScreen from '../screens/OrganizationProfileScreen';


import * as navigationService from '../services/navigationService';
import { globalStyles } from '../styles/globalStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { FeatureFlagsProvider } from '../contexts/FeatureFlagsContext';
import { CreditEconomyProvider } from '../contexts/CreditEconomyContext';
import { AppLayoutProvider, FIXED_FOOTER_HEIGHT, fixedWidgetBottomInset, EXPENSE_TRACKER_ANIM_MS, EXPENSE_TRACKER_ANIM_EASING, expenseTrackerFixedInset, useAppLayout, useViewportWidth } from '../contexts/AppLayoutContext';
import ExpenseTrackerModal from '../components/ExpenseTrackerModal';
import { useFixedFooter, useUpdatedColorScheme } from '../contexts/FeatureFlagsContext';
import { BREAKPOINTS } from '../styles/breakpoints';
import { getPagePaletteIfEnabled } from '../constants/pageColors';
import {
  canvasFromSlug,
  parseCalendarPath,
  rewriteCalendarPath,
  slugFromCanvas,
} from '../utils/calendarRoutes';

const Stack = createNativeStackNavigator();

function useMobileViewport() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= BREAKPOINTS.mobile;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const check = () => setIsMobile(window.innerWidth <= BREAKPOINTS.mobile);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}

const webOriginPrefix = Platform.OS === 'web' && typeof window !== 'undefined' ? String(window.location.origin || '') : '';

function queryParamsFromPath(path) {
  const q = String(path || '').split('?')[1] || '';
  const out = {};
  q.split('&').filter(Boolean).forEach((part) => {
    const [k, v] = part.split('=');
    if (!k) return;
    try {
      out[decodeURIComponent(k)] = decodeURIComponent((v || '').replace(/\+/g, ' '));
    } catch {
      out[k] = v || '';
    }
  });
  return out;
}

const linking = {
  prefixes: ['http://localhost:19006', 'http://localhost:19007', 'http://localhost:19008', 'https://vrc.app', webOriginPrefix].filter(Boolean),
  config: {
    screens: {
      Landing: 'landing',
      Login: 'login',
      Home: 'home',
      Profile: 'profile',
      MyReports: 'my-reports',
      VenueList: 'venues',
      Venue: 'venue/:venueId',
      VenueSubmission: 'venue-submission',
      Admin: 'admin',
      AddVenue: 'add-venue',
      Waitlist: 'waitlist',
      TourPlanner: 'tour-planner',
      MyCalendar: {
        path: 'calendar/:canvas?/:dateKey?',
        parse: {
          canvas: (value) => canvasFromSlug(value) || value,
          dateKey: (value) => value,
        },
        stringify: {
          canvas: (value) => slugFromCanvas(value),
          dateKey: (value) => value || '',
        },
      },
      AboutUs: 'about-us',
      Privacy: 'privacy',
      Terms: 'terms',
      MutualAidFund: 'fund',
      ReviewNotes: 'review',
      NotifyWhenLive: 'coming-soon/:featureId',
      Plans: {
        path: 'plans',
        parse: {
          ref: (ref) => (ref != null ? String(ref) : undefined),
        },
      },
      SubscriptionSuccess: 'subscription-success',
      ManageSubscription: 'manage-subscription',
      Wallet: 'wallet',
      // Legacy aliases handled in getStateFromPath
      Balance: 'balance',
      Contact: 'contact',
      CommunityScreen: 'community',
      CommunityList: 'community/list',
      CommunityPostCreate: 'community/post/create',
      ProfileCardEdit: 'profile/card/edit',
      GearSwapList: 'community/gear-swap',
      EventsList: 'community/events',
      GearSwapPostCreate: 'community/gear-swap/create',
      UserProfileScreen: 'community/user/:userId',
      SharedTourView: 'tour/:tourId',
      CalendarDayDetails: 'calendar/day/:dateKey',
      CalendarShareView: 'calendar/share/:token',
    },
  },
  getStateFromPath(path, options) {
    const raw = String(path || '').replace(/^\//, '');
    // Legacy /stripe and /balance → /wallet
    if (raw === 'stripe' || raw.startsWith('stripe?') || raw.startsWith('stripe/')) {
      return getStateFromPath(raw.replace(/^stripe/, 'wallet'), options);
    }
    if (raw === 'balance' || raw.startsWith('balance?') || raw.startsWith('balance/')) {
      return getStateFromPath(raw.replace(/^balance/, 'wallet'), options);
    }
    const rewritten = rewriteCalendarPath(path);
    const parsed = parseCalendarPath(rewritten);
    if (parsed.kind === 'share') {
      return { routes: [{ name: 'CalendarShareView', params: { token: parsed.token } }] };
    }
    if (parsed.kind === 'day') {
      return {
        routes: [{
          name: 'CalendarDayDetails',
          params: { dateKey: parsed.dateKey, ...queryParamsFromPath(rewritten) },
        }],
      };
    }
    return getStateFromPath(rewritten, options);
  },
};

// Auth required wrapper component
function AuthRequired({ children }) {
  const { user, loading } = useUser();
  const navigation = useNavigation();
  const route = useRoute();

  const routeName = route?.name || 'Home';
  const returnCanvas = route?.params?.canvas;
  const returnDateKey = route?.params?.dateKey;
  const returnToken = route?.params?.token;

  const sendToLogin = () => {
    navigation.replace('Login', {
      message: 'Please sign in to access this feature',
      returnTo: routeName,
      returnParams: route?.params || undefined,
    });
  };

  useEffect(() => {
    if (loading || user) return undefined;
    // On a direct URL load the stack is still initialising when this effect runs, and its own
    // setup overwrites a replace dispatched now (guest saw a blank page). Defer one tick.
    const timer = setTimeout(sendToLogin, 0);
    return () => clearTimeout(timer);
  }, [user, loading, navigation, routeName, returnCanvas, returnDateKey, returnToken, route?.params]);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.8)' }}>
        <ActivityIndicator size="large" color="#ffffff" />
        <Text style={{ color: '#ffffff', marginTop: 10, fontFamily: 'VastagoGrotesk' }}>
          Please wait...
        </Text>
      </View>
    );
  }

  if (!user) {
    // Normally replaced by Login within a tick; never leave a guest on a blank page.
    return (
      <View style={{ flex: 1, minHeight: '100dvh', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 16, marginBottom: 16, textAlign: 'center' }}>
          Please sign in to access this feature
        </Text>
        <Pressable
          accessibilityRole="button"
          onPress={sendToLogin}
          style={{ backgroundColor: '#1075ae', paddingVertical: 12, paddingHorizontal: 24, borderRadius: 8 }}
        >
          <Text style={{ color: '#ffffff', fontFamily: 'VastagoGrotesk', fontSize: 15 }}>Sign in</Text>
        </Pressable>
      </View>
    );
  }

  return children;
}

/**
 * The viewport-pinned footer. While its screen is focused it tells AppLayoutContext the
 * footer is pinned, so fixed corner widgets (Review VRC) sit above it. Screens that hide
 * the footer, unpin it, or lock the viewport never render this, so the flag stays false.
 */
function PinnedFooter(props) {
  const isFocused = useIsFocused();
  const { claimFooterPinned, releaseFooterPinned } = useAppLayout();
  const ownerRef = useRef(null);
  if (!ownerRef.current) ownerRef.current = {};
  const pinned = !props.isAdminPage;
  useEffect(() => {
    if (!isFocused) return undefined;
    const owner = ownerRef.current;
    claimFooterPinned(owner, pinned);
    return () => releaseFooterPinned(owner);
  }, [isFocused, pinned, claimFooterPinned, releaseFooterPinned]);
  return <Footer {...props} pinned />;
}

// Landing screen with header and footer
function LandingScreenContainer(props) {
  const { headerHeight } = useAppLayout();
  const fixedFooter = useFixedFooter();
  const isMobile = useMobileViewport();
  const pinFooter = fixedFooter && !isMobile;

  return (
    <View style={{ flex: 1, minHeight: '100dvh', flexDirection: 'column' }}>
      <HeaderBar />
      <ScrollView
        nativeID="scroll-view"
        style={{ flex: 1 }}
        contentContainerStyle={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: pinFooter ? FIXED_FOOTER_HEIGHT : 0,
        }}
      >
        <View style={{ height: headerHeight, width: '100%' }} />
        <View style={{ flex: 1 }}>
          <LandingScreen {...props} />
        </View>
        {!pinFooter && <Footer />}
      </ScrollView>
      {pinFooter && <PinnedFooter />}
    </View>
  );
}

function ScreenWithHeader({
  children,
  isAdminPage = false,
  hideHeader = false,
  hideFooter = false,
  lockViewport = false,
  requireAuth = false,
  onScroll,
  scrollEventThrottle = 16,
  footerBg,
  scrollable = true,
  pinFooter: pinFooterOverride,
  contentFill = true,
  contentPadBottom = 0,
}) {
  const { headerHeight } = useAppLayout();
  const fixedFooter = useFixedFooter();
  const isMobile = useMobileViewport();
  const pinFooter = pinFooterOverride === false ? false : (fixedFooter && !isMobile);
  const topSpacerHeight = hideHeader ? 0 : headerHeight;
  const showFooter = !hideFooter;
  const footerPad = (pinFooter && showFooter ? FIXED_FOOTER_HEIGHT : 0) + Number(contentPadBottom || 0);
  const contentWrapStyle = contentFill ? { flex: 1, minHeight: 0 } : null;

  const scrollBody = (
    <View style={{ flex: 1, minHeight: '100dvh', flexDirection: 'column' }}>
      {!hideHeader && <HeaderBar />}
      <ScrollView
        nativeID="scroll-view"
        style={{ flex: 1 }}
        contentContainerStyle={{
          ...(contentFill ? { flexGrow: 1 } : null),
          paddingBottom: footerPad,
          ...(Platform.OS === 'web' ? { overscrollBehavior: 'auto' } : null),
        }}
        onScroll={onScroll}
        scrollEventThrottle={scrollEventThrottle}
      >
        {topSpacerHeight > 0 ? <View style={{ height: topSpacerHeight, width: '100%' }} /> : null}
        <View style={contentWrapStyle}>{children}</View>
        {!pinFooter && showFooter && <Footer isAdminPage={isAdminPage} backgroundColor={footerBg} pinned={false} />}
      </ScrollView>
      {pinFooter && showFooter && <PinnedFooter isAdminPage={isAdminPage} backgroundColor={footerBg} />}
    </View>
  );

  const flexBody = (
    <View style={{ flex: 1, minHeight: '100dvh', flexDirection: 'column' }}>
      {!hideHeader && <HeaderBar />}
      <View
        nativeID="scroll-view"
        style={{
          flex: 1,
          minHeight: 0,
          paddingBottom: footerPad,
          ...(Platform.OS === 'web' ? { overflow: lockViewport ? 'hidden' : 'auto' } : {}),
        }}
        {...(Platform.OS === 'web' && !lockViewport ? { dataSet: { appScroll: 'true' } } : {})}
      >
        {topSpacerHeight > 0 ? <View style={{ height: topSpacerHeight, width: '100%' }} /> : null}
        <View style={contentWrapStyle}>{children}</View>
        {!pinFooter && showFooter && <Footer isAdminPage={isAdminPage} backgroundColor={footerBg} pinned={false} />}
      </View>
      {pinFooter && showFooter && <PinnedFooter isAdminPage={isAdminPage} backgroundColor={footerBg} />}
    </View>
  );

  if (requireAuth) {
    return <AuthRequired>{scrollable ? scrollBody : flexBody}</AuthRequired>;
  }

  return scrollable ? scrollBody : flexBody;
}

function HomeScreen(props) {
  return (
    <ScreenWithHeader>
      <Home {...props} />
    </ScreenWithHeader>
  );
}

function ComingSoonScreen({ featureId }) {
  return (
    <ScreenWithHeader>
      <NotifyWhenLive featureId={featureId} />
    </ScreenWithHeader>
  );
}

function ComingSoonOr({ featureId, children }) {
  const { user } = useUser();
  const unlocked = useComingSoonFeatureUnlocked(featureId, user);
  if (!unlocked) {
    return <ComingSoonScreen featureId={featureId} />;
  }
  return children;
}

function ProfileScreen(props) {
  return (
    <ComingSoonOr featureId="account">
      <ScreenWithHeader requireAuth={true}>
        <Profile {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function VenueListScreen(props) {
  const venueListRef = useRef(null);
  
  const handleScroll = (event) => {
    // Call VenueList's scroll handler if it exists
    if (venueListRef.current?.handleScrollInternal) {
      venueListRef.current.handleScrollInternal(event);
    }
  };

  return (
    <ScreenWithHeader onScroll={handleScroll}>
      <VenueList {...props} ref={venueListRef} />
    </ScreenWithHeader>
  );
}

function VenueDetailScreen(props) {
  const { isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const footerBg = getPagePaletteIfEnabled('venue', isDarkMode, updatedColorScheme)?.footerBackground ?? '#3D7FE6';
  return (
    <ScreenWithHeader footerBg={footerBg}>
      <VenueScreen {...props} />
    </ScreenWithHeader>
  );
}

function VenueSubmissionScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <VenueSubmission {...props} />
    </ScreenWithHeader>
  );
}

function AdminScreen(props) {
  const { user, loading } = useUser();
  const navigation = useNavigation();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        // Redirect to login if not authenticated
        navigation.replace('Login', {
          message: 'Please sign in to access this feature'
        });
      } else if (user.userType !== 'admin') {
        // Redirect non-admin users away from admin page immediately
        navigation.replace('Home', {
          message: 'Access denied. Admin privileges required.'
        });
      }
    }
  }, [user, loading, navigation]);

  // Show loading while checking auth
  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.8)' }}>
        <ActivityIndicator size="large" color="#ffffff" />
        <Text style={{ color: '#ffffff', marginTop: 10, fontFamily: 'VastagoGrotesk' }}>
          Loading...
        </Text>
      </View>
    );
  }

  // Don't render admin screen if user is not authenticated or not an admin
  if (!user || user.userType !== 'admin') {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.8)' }}>
        <ActivityIndicator size="large" color="#ffffff" />
        <Text style={{ color: '#ffffff', marginTop: 10, fontFamily: 'VastagoGrotesk' }}>
          Redirecting...
        </Text>
      </View>
    );
  }

  // Only render admin screen for authenticated admin users
  return (
    <ScreenWithHeader isAdminPage={true} hideHeader={true} requireAuth={true} scrollable={false}>
      <Admin {...props} />
    </ScreenWithHeader>
  );
}

function AddVenueScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <AddVenue {...props} />
    </ScreenWithHeader>
  );
}

function MyReportsScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <MyReports {...props} />
    </ScreenWithHeader>
  );
}

function useTourPlannerMobileLayout() {
  const [isTourPlannerMobile, setIsTourPlannerMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= BREAKPOINTS.tablet;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const check = () => setIsTourPlannerMobile(window.innerWidth <= BREAKPOINTS.tablet);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isTourPlannerMobile;
}

function TourPlannerScreen(props) {
  const isTourPlannerMobile = useTourPlannerMobileLayout();

  return (
    <ComingSoonOr featureId="map">
      {/* Lauren 2026-10-29: the Map is not public; guests sign in and come back here. */}
      <ScreenWithHeader
        requireAuth={true}
        scrollable={!isTourPlannerMobile}
        hideFooter
        lockViewport={isTourPlannerMobile}
      >
        <TourPlanner {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function MyCalendarScreen(props) {
  const { isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const footerBg = updatedColorScheme
    ? getPagePaletteIfEnabled('calendar', isDarkMode, true)?.footerBackground
    : undefined;
  return (
    <ComingSoonOr featureId="calendar">
      <ScreenWithHeader requireAuth={true} footerBg={footerBg} pinFooter={false} scrollable contentFill={false}>
        <MyCalendar {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function CalendarDayDetailsScreen(props) {
  return (
    <ComingSoonOr featureId="calendar">
      <ScreenWithHeader scrollable={true}>
        <CalendarDayDetails {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function CalendarShareViewScreen(props) {
  const { isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const footerBg = updatedColorScheme
    ? getPagePaletteIfEnabled('calendar', isDarkMode, true)?.footerBackground
    : undefined;
  return (
    <ScreenWithHeader scrollable contentFill={false} pinFooter={false} footerBg={footerBg} contentPadBottom={96}>
      <CalendarShareView {...props} />
    </ScreenWithHeader>
  );
}

function AboutUsScreen(props) {
  return (
    <ScreenWithHeader>
      <AboutUs {...props} />
    </ScreenWithHeader>
  );
}

function PrivacyScreen(props) {
  return (
    <ScreenWithHeader>
      <PrivacyPolicy {...props} />
    </ScreenWithHeader>
  );
}

function TermsScreen(props) {
  return (
    <ScreenWithHeader>
      <TermsOfUse {...props} />
    </ScreenWithHeader>
  );
}

function MutualAidFundScreen(props) {
  return (
    <ComingSoonOr featureId="fund">
      <ScreenWithHeader scrollable={false}>
        <MutualAidFund {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function ReviewNotesScreen() {
  return (
    <ScreenWithHeader scrollable={false} hideFooter contentFill>
      <ReviewNotes />
    </ScreenWithHeader>
  );
}

function NotifyWhenLiveScreen(props) {
  return (
    <ScreenWithHeader>
      <NotifyWhenLive {...props} />
    </ScreenWithHeader>
  );
}

function PlansScreen(props) {
  return (
    <ScreenWithHeader>
      <Plans {...props} />
    </ScreenWithHeader>
  );
}

function SubscriptionSuccessScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <SubscriptionSuccess {...props} />
    </ScreenWithHeader>
  );
}

function ManageSubscriptionScreen(props) {
  return (
    <ScreenWithHeader requireAuth={true}>
      <Balance {...props} route={{ ...props.route, params: { ...props.route?.params, tab: 'membership' } }} />
    </ScreenWithHeader>
  );
}

function WalletScreen(props) {
  const page = (
    <ScreenWithHeader requireAuth={true}>
      <Balance {...props} />
    </ScreenWithHeader>
  );
  // Members reach their plan through Wallet's membership tab; that stays open while Wallet is hidden.
  if (props.route?.params?.tab === 'membership') return page;
  return (
    <ComingSoonOr featureId="balance">
      <ScreenWithHeader requireAuth={true}>
        <Balance {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}



function ContactScreen(props) {
  return (
    <ScreenWithHeader>
      <Contact {...props} />
    </ScreenWithHeader>
  );
}

function CommunityScreenContainer(props) {
  const { isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const footerBg = getPagePaletteIfEnabled('gearSwap', isDarkMode, updatedColorScheme)?.footerBackground ?? '#FF7A00';
  return (
    <ComingSoonOr featureId="directory">
      <ScreenWithHeader footerBg={footerBg}>
        <GearSwapList {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function CommunityListScreen(props) {
  const communityListRef = useRef(null);
  
  const handleScroll = (event) => {
    // Call CommunityList's scroll handler if it exists
    if (communityListRef.current?.handleScrollInternal) {
      communityListRef.current.handleScrollInternal(event);
    }
  };

  return (
    <ComingSoonOr featureId="directory">
      <ScreenWithHeader requireAuth={true} footerBg="#1075ae" onScroll={handleScroll}>
        <CommunityList {...props} ref={communityListRef} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function CommunityPostCreateScreen(props) {
  return (
    <ComingSoonOr featureId="directory">
      <ScreenWithHeader footerBg="#1075ae">
        <CommunityPostCreate {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function ProfileCardEditScreen(props) {
  return (
    <ComingSoonOr featureId="directory">
      <ScreenWithHeader footerBg="#1075ae">
        <ProfileCardEdit {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function EventsListScreen(props) {
  return (
    <ComingSoonOr featureId="directory">
      <ScreenWithHeader footerBg="#231f20">
        <EventsList {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function GearSwapListScreen(props) {
  const { isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const footerBg = getPagePaletteIfEnabled('gearSwap', isDarkMode, updatedColorScheme)?.footerBackground ?? '#ffffff';
  return (
    <ComingSoonOr featureId="gear">
      <ScreenWithHeader footerBg={footerBg}>
        <GearSwapList {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function GearSwapPostCreateScreen(props) {
  const { isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const footerBg = getPagePaletteIfEnabled('gearSwap', isDarkMode, updatedColorScheme)?.footerBackground ?? '#ffffff';
  return (
    <ComingSoonOr featureId="gear">
      <ScreenWithHeader footerBg={footerBg}>
        <GearSwapPostCreate {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function UserProfileScreenContainer(props) {
  return (
    <ComingSoonOr featureId="directory">
      <ScreenWithHeader>
        <UserProfileScreen {...props} />
      </ScreenWithHeader>
    </ComingSoonOr>
  );
}

function SharedTourViewScreen(props) {
  return (
    <ComingSoonOr featureId="map">
      <SharedTourView {...props} />
    </ComingSoonOr>
  );
}

function OrganizationProfileScreenContainer(props) {
  return (
    <ScreenWithHeader>
      <OrganizationProfileScreen {...props} />
    </ScreenWithHeader>
  );
}

function NavigationContent() {
  const { user, loading } = useUser();
  const { theme } = useTheme();
  const { expenseTrackerOpen: trackerRequested, setExpenseTrackerOpen, footerPinned } = useAppLayout();
  // Budget is for signed-up users only; a guest never gets the rail, even if something opens it.
  const expenseTrackerOpen = trackerRequested && !!user;
  useEffect(() => {
    if (!user && trackerRequested) setExpenseTrackerOpen(false);
  }, [user, trackerRequested, setExpenseTrackerOpen]);
  const viewportWidth = useViewportWidth();
  const railOpenWidth = expenseTrackerFixedInset(true, viewportWidth);
  const railWidth = expenseTrackerOpen ? railOpenWidth : 0;
  // REQ-070: keep the Review VRC button above the footer only when the focused screen
  // really pins one (not on hideFooter / pinFooter={false} / lockViewport screens).
  const reviewButtonBottomInset = fixedWidgetBottomInset(footerPinned, FIXED_FOOTER_HEIGHT);
  return (
    <View id="navigation-container" style={{ flex: 1, flexDirection: 'row', width: '100%', minHeight: '100dvh' }}>
      <View
        style={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          minHeight: '100dvh',
          overflow: 'hidden',
        }}
      >
      <Stack.Navigator 
        initialRouteName={user ? "Home" : "Login"}
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: theme.background.primary },
          animation: 'slide_from_right',
          presentation: 'card'
        }}
      >
      {/* Landing screen that bypasses login */}
      <Stack.Screen 
        name="Landing" 
        component={LandingScreenContainer} 
        options={{
          gestureEnabled: false,
          animationEnabled: false
        }}
      />
      
      <Stack.Group>
        <Stack.Screen 
          name="Login" 
          component={LoginScreen} 
          options={{
            gestureEnabled: false,
            animationEnabled: false
          }}
        />
        <Stack.Screen 
          name="Waitlist" 
          component={WaitlistScreen}
          options={{
            gestureEnabled: false,
            animationEnabled: false
          }}
        />
      </Stack.Group>

      <Stack.Group screenOptions={{ gestureEnabled: true, contentStyle: { backgroundColor: theme.background.primary } }}>
        <Stack.Screen name="Home" component={HomeScreen} />
        <Stack.Screen name="Profile" component={ProfileScreen} />
        <Stack.Screen name="MyReports" component={MyReportsScreen} />
        <Stack.Screen name="VenueList" component={VenueListScreen} />
        <Stack.Screen name="Venue" component={VenueDetailScreen} />
        <Stack.Screen name="VenueSubmission" component={VenueSubmissionScreen} />
        <Stack.Screen name="Admin" component={AdminScreen} />
        <Stack.Screen name="AddVenue" component={AddVenueScreen} />
        <Stack.Screen name="TourPlanner" component={TourPlannerScreen} />
        <Stack.Screen name="MyCalendar" component={MyCalendarScreen} />
        <Stack.Screen name="CalendarDayDetails" component={CalendarDayDetailsScreen} />
        <Stack.Screen name="CalendarShareView" component={CalendarShareViewScreen} options={{ headerShown: false }} />
        <Stack.Screen name="AboutUs" component={AboutUsScreen} />
        <Stack.Screen name="Privacy" component={PrivacyScreen} options={{ title: 'Privacy Policy | Venue Report Card' }} />
        <Stack.Screen name="Terms" component={TermsScreen} options={{ title: 'Terms of Use | Venue Report Card' }} />
        <Stack.Screen name="MutualAidFund" component={MutualAidFundScreen} />
        <Stack.Screen name="ReviewNotes" component={ReviewNotesScreen} />
        <Stack.Screen name="NotifyWhenLive" component={NotifyWhenLiveScreen} />
        <Stack.Screen name="Plans" component={PlansScreen} />
        <Stack.Screen name="SubscriptionSuccess" component={SubscriptionSuccessScreen} />
        <Stack.Screen name="ManageSubscription" component={ManageSubscriptionScreen} />
        <Stack.Screen name="Wallet" component={WalletScreen} />
        <Stack.Screen name="Balance" component={WalletScreen} />


        <Stack.Screen name="Contact" component={ContactScreen} />
        <Stack.Screen name="CommunityScreen" component={CommunityScreenContainer} />
        <Stack.Screen name="CommunityList" component={CommunityListScreen} />
        <Stack.Screen name="CommunityPostCreate" component={CommunityPostCreateScreen} />
        <Stack.Screen name="ProfileCardEdit" component={ProfileCardEditScreen} />
        <Stack.Screen name="GearSwapList" component={GearSwapListScreen} />
        <Stack.Screen name="EventsList" component={EventsListScreen} />
        <Stack.Screen name="GearSwapPostCreate" component={GearSwapPostCreateScreen} />
        <Stack.Screen name="UserProfileScreen" component={UserProfileScreenContainer} />
        <Stack.Screen name="OrganizationProfileScreen" component={OrganizationProfileScreenContainer} />
        <Stack.Screen name="SharedTourView" component={SharedTourViewScreen} options={{ headerShown: false }} />


      </Stack.Group>
      </Stack.Navigator>
      <VersionWidget />
      <TesterSwitcher />
      <ReviewVrcButton rightInset={railWidth} bottomInset={reviewButtonBottomInset} />
      </View>
      <View
        pointerEvents={expenseTrackerOpen ? 'auto' : 'none'}
        style={{
          width: railWidth,
          flexShrink: 0,
          height: '100%',
          minHeight: '100dvh',
          overflow: 'hidden',
          ...(Platform.OS === 'web'
            ? { transition: `width ${EXPENSE_TRACKER_ANIM_MS}ms ${EXPENSE_TRACKER_ANIM_EASING}` }
            : null),
        }}
      >
        <View style={{ width: railOpenWidth, height: '100%', minHeight: '100dvh' }}>
          <ExpenseTrackerModal
            visible={expenseTrackerOpen}
            onClose={() => setExpenseTrackerOpen(false)}
          />
        </View>
      </View>
    </View>
  );
}

export default function Navigation() {
  const navigationRef = useRef();
  const [dimensions, setDimensions] = React.useState({
    window: Dimensions.get('window')
  });

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions({ window });
    });
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    if (navigationRef.current) {
      navigationService.setNavigator(navigationRef.current);
    }
  }, [navigationRef.current]);

  return (
    <NavigationContainer 
      ref={navigationRef}
      linking={Platform.OS === 'web' ? linking : undefined}
      fallback={<LoadingScreen />}
    >
      <LoginPersistenceProvider>
        <FeatureFlagsProvider>
          <CreditEconomyProvider>
          <AppLayoutProvider>
            <VersionWidgetProvider>
              <WhatsNewProvider>
                <VenueProvider>
                  <CommunityProvider>
                    <CommunityModalProvider>
                      <NavigationContent />
                    </CommunityModalProvider>
                  </CommunityProvider>
                </VenueProvider>
              </WhatsNewProvider>
            </VersionWidgetProvider>
          </AppLayoutProvider>
          </CreditEconomyProvider>
        </FeatureFlagsProvider>
      </LoginPersistenceProvider>
    </NavigationContainer>
  );
}

function LoadingScreen() {
  return (
    <View style={[globalStyles.container, { justifyContent: 'center', alignItems: 'center' }]}>
      <BackgroundVideo />
      <View style={[globalStyles.overlay, { justifyContent: 'center', alignItems: 'center' }]}>
        <Image
          source={require('../../assets/logos/logo.png')}
          style={{ width: 150, height: 150, marginBottom: 20 }}
        />
        <ActivityIndicator size="large" color="#ffffff" />
      </View>
    </View>
  );
} 